import axios from 'axios';

import { objectToSearchParams } from '@hotjar/webapp/lib/url-search-params';
import { ALL_HIGHLIGHTS_ID } from '@hotjar/webapp/domains/collections/model';

import {
  mapHighlightDtoToModel,
  mapHighlightFiltersModelToDto,
  mapHighlightsListItemDtoToModel,
  mapHighlightEventMetadataModelToDto,
} from './mapper';
import type {
  Highlight as HighlightModel,
  HighlightFilters,
  HighlightsListItem as HighlightsListItemModel,
  HighlightEventMetadata,
  UIPosition,
} from './model';
import type {
  Highlight as HighlightDto,
  HighlightsListItem as HighlightsListItemDto,
  HighlightsEventType,
  UnseenCount as UnseenCountDto,
  HighlightEventMetadata as HighlightEventMetadataDto,
} from './dto';
import type { PaginatedResponse as PaginatedResponseDto } from './common/dto';
import { getPath } from './common/api';
import type { SiteId, HighlightId } from './common/api';

export type { UIPosition, HighlightArea } from './model';

export const HIGHLIGHTS_LIST_QUERY_KEY = 'highlights-list';

export const HEATMAP_HIGHLIGHTS_LIST_QUERY_KEY = 'heatmap-highlights-list';

export const SINGLE_HIGHLIGHT_QUERY_KEY = 'highlight';

export const RECORDING_HIGHLIGHTS_LIST_QUERY_KEY = 'highlights-on-a-recording-list';

export const UNSEEN_HIGHLIGHTS_COUNT = 'highlights-unseen-count';

export const RECORDING_HIGHLIGHT_STATUS = 'highlight-status';

export async function getHighlightsList(
  siteId: SiteId,
  collectionId: string | undefined,
  queryParams: HighlightFilters,
): Promise<{ items: Array<HighlightsListItemModel>; totalCount: number }> {
  const params = objectToSearchParams(mapHighlightFiltersModelToDto(queryParams));

  if (collectionId && collectionId !== ALL_HIGHLIGHTS_ID) {
    params.append('collection_ids', collectionId);
  }

  const response = await axios.get<PaginatedResponseDto<HighlightsListItemDto>>(getPath(siteId), {
    params,
  });

  return {
    items: response.data.items.map(mapHighlightsListItemDtoToModel),
    totalCount: response.data.item_count,
  };
}

export async function getHighlight(
  siteId: SiteId,
  highlightId: HighlightId,
): Promise<HighlightModel> {
  const path = `${getPath(siteId)}/${highlightId}`;
  const response = await axios.get<HighlightDto>(path);

  return mapHighlightDtoToModel(response.data);
}

export async function removeHighlight(
  siteId: SiteId,
  highlightId: HighlightId,
  uiPosition?: UIPosition,
): Promise<void> {
  const path = `${getPath(siteId)}/${highlightId}`;

  // eslint-disable-next-line camelcase
  await axios.delete<{ success: true }>(path, {
    params: uiPosition ? { ui_position: uiPosition } : {},
  });
}

export async function addLabel(
  siteId: SiteId,
  highlightId: HighlightId,
  labelId: string,
): Promise<void> {
  const path = `${getPath(siteId)}/${highlightId}/label`;
  // eslint-disable-next-line camelcase
  await axios.post<{ label_id: number }>(path, {
    label_id: Number(labelId),
  });
}

export async function removeLabel(
  siteId: SiteId,
  highlightId: HighlightId,
  labelId: string,
): Promise<void> {
  const path = `${getPath(siteId)}/${highlightId}/label/${labelId}`;
  // eslint-disable-next-line camelcase
  await axios.delete(path);
}

export async function getUnseenHighlightsCount(siteId: SiteId): Promise<number> {
  const path = `${getPath(siteId)}/unseen`;
  // eslint-disable-next-line camelcase
  const response = await axios.get<UnseenCountDto>(path);

  return response.data.unseen_count;
}

interface HighlightEventPayload {
  event: HighlightsEventType;
  // eslint-disable-next-line camelcase
  highlight_id: string;
  metadata?: HighlightEventMetadataDto;
}

export function sendHighlightEvent(
  siteId: SiteId,
  data: { highlightId: string; eventType: HighlightsEventType },
  metadata?: HighlightEventMetadata,
): Promise<void> {
  const { highlightId, eventType } = data;
  const path = `${getPath(siteId, 'v2')}/events`;

  const payload: HighlightEventPayload = {
    event: eventType,
    highlight_id: highlightId,
    metadata: mapHighlightEventMetadataModelToDto(metadata),
  };

  return axios.post(path, payload);
}

export async function setHighlightSolvedStatus(
  siteId: SiteId,
  highlightId: HighlightId,
  data: {
    solved: boolean;
    uiPosition?: UIPosition;
  },
): Promise<void> {
  const path = `${getPath(siteId)}/${highlightId}/solve`;
  await axios.patch(path, {
    solved: data.solved,
    // eslint-disable-next-line camelcase
    ui_position: data.uiPosition,
  });
}

export { createHeatmapHighlight, getHeatmapHighlights } from './heatmap/api';

export { createRecordingHighlight, getRecordingHighlights } from './recording/api';
